import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import type { AdminUserListItem, UserToolAccess } from '../../types/users';

/**
 * Tool abilitati per un utente. In modalita' "approve" il salvataggio approva
 * anche l'utente con i tool selezionati, in un'unica chiamata.
 */
interface Props {
    user: Pick<AdminUserListItem, 'id' | 'email' | 'displayName'> | null;
    approve?: boolean;
    onClose: () => void;
    onSaved: (approvedUser?: AdminUserListItem) => void;
}

const UserToolsModal: React.FC<Props> = ({ user, approve = false, onClose, onSaved }) => {
    const { showToast } = useToast();
    const [tools, setTools] = useState<UserToolAccess[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!user) return;
        setLoading(true);
        setTools([]);
        setQuery('');
        apiService.adminGetUserTools<UserToolAccess>(user.id)
            .then(setTools)
            .catch(() => { showToast('Errore', 'Impossibile caricare i tool dell’utente', 'danger'); onClose(); })
            .finally(() => setLoading(false));
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? tools.filter(t => `${t.toolName} ${t.toolCode}`.toLowerCase().includes(q)) : tools;
    }, [tools, query]);
    const enabledCount = tools.filter(t => t.isEnabled).length;

    const toggle = (toolId: number) =>
        setTools(list => list.map(t => t.toolId === toolId ? { ...t, isEnabled: !t.isEnabled } : t));

    const save = async () => {
        if (!user) return;
        const ids = tools.filter(t => t.isEnabled).map(t => t.toolId);
        setSaving(true);
        try {
            if (approve) {
                const updated = await apiService.adminSetUserApproval<AdminUserListItem>(user.id, 'Approved', ids);
                showToast('Utente approvato', `${user.email} può accedere ai tool selezionati`, 'success');
                onSaved(updated);
            } else {
                await apiService.adminSetUserTools(user.id, ids);
                showToast('Tool aggiornati', `Permessi aggiornati per ${user.email}`, 'success');
                onSaved();
            }
        } catch (e: any) {
            showToast('Errore', e?.response?.data?.error ?? 'Salvataggio permessi non riuscito', 'danger');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={!!user} onHide={() => !saving && onClose()} size="lg" scrollable centered>
            <Modal.Header closeButton={!saving}>
                <div>
                    <Modal.Title>{approve ? 'Approva e assegna i tool' : 'Tool abilitati'}</Modal.Title>
                    <div className="text-body-secondary small">{user?.displayName ? `${user.displayName} · ` : ''}{user?.email}</div>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="filter-bar__search flex-grow-1 position-relative">
                        <i className="bi bi-search position-absolute top-50 translate-middle-y ms-2 text-body-secondary" aria-hidden="true" />
                        <input type="search" className="form-control form-control-sm ps-4" placeholder="Filtra i tool…"
                            aria-label="Filtra i tool" value={query} onChange={e => setQuery(e.target.value)} />
                    </div>
                    <span className="small text-body-secondary text-nowrap">{enabledCount} abilitati</span>
                </div>
                {loading ? (
                    <div className="et-state"><span className="spinner-border spinner-border-sm" /> Caricamento tool…</div>
                ) : (
                    <div className="list-group">
                        {visible.map(t => (
                            <label key={t.toolId} className="list-group-item d-flex align-items-start gap-3">
                                <input className="form-check-input mt-1" type="checkbox" checked={t.isEnabled} onChange={() => toggle(t.toolId)} />
                                <span className="flex-grow-1">
                                    <span className="d-flex flex-wrap align-items-center gap-2">
                                        <strong>{t.toolName}</strong>
                                        {t.visibility === 'Restricted' && <span className="badge bg-warning"><i className="bi bi-lock-fill me-1" />Ristretto</span>}
                                    </span>
                                    <span className="text-body-secondary small">{t.toolCode} · {t.toolType} · {t.audience}</span>
                                </span>
                            </label>
                        ))}
                        {visible.length === 0 && <div className="et-state">Nessun tool corrisponde.</div>}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Annulla</button>
                <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving || loading}>
                    {saving ? 'Salvataggio…' : approve ? 'Approva e salva' : 'Salva tool'}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserToolsModal;
